import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SUPPORTED = [
  { code: 'en', label: 'EN', name: 'English' },
  { code: 'fr', label: 'FR', name: 'Français' },
];

const LangContext = createContext(null);

const STORAGE_KEY = 'gyt.lang';

export const LangProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && SUPPORTED.find((l) => l.code === stored) ? stored : 'en';
    } catch (_) {
      return 'en';
    }
  });

  const [dict, setDict] = useState({}); // { en: {key: english}, fr: {key: french}, ... }
  const pendingRef = useRef(new Set()); // dedupe in-flight requests
  const batchRef = useRef([]); // strings queued for translation
  const timerRef = useRef(null);

  const setLang = (code) => {
    if (!SUPPORTED.find((l) => l.code === code)) return;
    try { localStorage.setItem(STORAGE_KEY, code); } catch (_) {}
    setLangState(code);
  };

  const flushBatch = async (target) => {
    const queued = batchRef.current;
    batchRef.current = [];
    if (!queued.length) return;
    const payload = {};
    for (const item of queued) payload[item.key] = item.english;
    try {
      const res = await fetch(`${API_URL}/api/i18n/translate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lang: target, strings: payload }),
      });
      if (!res.ok) throw new Error(`translate failed ${res.status}`);
      const data = await res.json();
      const tr = data[target] || {};
      setDict((prev) => ({
        ...prev,
        [target]: { ...(prev[target] || {}), ...tr },
      }));
    } catch (_) {
      // On failure, fall back to English (already stored)
    } finally {
      for (const item of queued) pendingRef.current.delete(`${target}::${item.key}`);
    }
  };

  const enqueue = (target, key, english) => {
    const guard = `${target}::${key}`;
    if (pendingRef.current.has(guard)) return;
    pendingRef.current.add(guard);
    batchRef.current.push({ key, english });
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => flushBatch(target), 80);
  };

  // t(key, english) — returns translated text. Falls back to english while waiting.
  const t = (key, english) => {
    if (lang === 'en') return english;
    const langDict = dict[lang] || {};
    if (langDict[key] !== undefined) return langDict[key];
    enqueue(lang, key, english);
    return english;
  };

  // Hydrate cache from sessionStorage for snappy revisits
  useEffect(() => {
    try {
      const cached = sessionStorage.getItem(`gyt.dict.${lang}`);
      if (cached) {
        const parsed = JSON.parse(cached);
        setDict((prev) => ({ ...prev, [lang]: { ...(prev[lang] || {}), ...parsed } }));
      }
    } catch (_) {}
  }, [lang]);

  // Persist current-lang dict to sessionStorage
  useEffect(() => {
    try {
      if (dict[lang]) sessionStorage.setItem(`gyt.dict.${lang}`, JSON.stringify(dict[lang]));
    } catch (_) {}
  }, [dict, lang]);

  const value = useMemo(
    () => ({ lang, setLang, t, supported: SUPPORTED }),
    [lang, dict] // re-create when dict changes so consumers re-render
  );

  return <LangContext.Provider value={value}>{children}</LangContext.Provider>;
};

export const useLang = () => {
  const ctx = useContext(LangContext);
  if (!ctx) throw new Error('useLang must be used within a LangProvider');
  return ctx;
};

export const useT = () => useLang().t;
